import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  NgZone
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { AuthService } from '@core/auth/auth.service';
import { SavedFieldDirective } from '@shared/directives/saved-field.directive';
import { LocalProfileService } from '@shared/services/local-profile.service';
import { PendingGroupInvite, ShoppingGroupDetails, UserProfile } from '@core/models/models';

declare const google: any;

@Component({
  selector: 'app-account-modals',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, SavedFieldDirective],
  template: `
    <!-- 1. Google Sign-In Modal -->
    @if (modalType === 'login') {
      <div class="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="login-dialog-title" (click)="close()">
        <div class="modal-card modal-card--login" (click)="$event.stopPropagation()">
          <header class="modal-header">
            <div class="title-with-icon">
              <svg class="login-title-icon" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#1f6435" stroke-width="2">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                <polyline points="10 17 15 12 10 7"></polyline>
                <line x1="15" y1="12" x2="3" y2="12"></line>
              </svg>
              <h2 id="login-dialog-title">התחברות ל-Commpare</h2>
            </div>
            <button type="button" class="modal-close-btn" (click)="close()" aria-label="סגירה">×</button>
          </header>

          <div class="modal-body">
            <p class="login-intro">
              התחברו באמצעות חשבון Google כדי לשמור סלי קניות, לשתף אותם עם משפחה וחברים, ולסנכרן בין מכשירים.
            </p>

            <div id="google-signin-btn-container" class="google-btn-wrapper"></div>

            <p class="login-terms-note">
              בהתחברות את/ה מסכים/ה ל<a routerLink="/terms" (click)="close()">תנאי השימוש</a> ול<a routerLink="/privacy" (click)="close()">מדיניות הפרטיות</a>.
            </p>

            @if (loggingIn) {
              <p class="login-loading-hint">מתחבר למערכת…</p>
            }

            @if (loginError) {
              <p class="error-msg" role="alert">{{ loginError }}</p>
            }
          </div>
        </div>
      </div>
    }

    <!-- 2. Profile Settings Modal -->
    @if (modalType === 'profile' && user) {
      <div class="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="profile-dialog-title" (click)="close()">
        <div class="modal-card" (click)="$event.stopPropagation()">
          <header class="modal-header">
            <div class="title-with-icon">
              <span class="user-avatar-badge">
                @if (user.pictureUrl) {
                  <img [src]="user.pictureUrl" alt="תמונת פרופיל" class="avatar-img" />
                } @else {
                  <span class="avatar-fallback">{{ (user.givenName || user.email || 'U')[0] }}</span>
                }
              </span>
              <div>
                <h2 id="profile-dialog-title">הפרטים שלי</h2>
                <span class="profile-subtitle">עריכת פרטים אישיים מחשבון Google</span>
              </div>
            </div>
            <button type="button" class="modal-close-btn" (click)="close()" aria-label="סגירה">×</button>
          </header>

          <form class="modal-body" (submit)="$event.preventDefault(); saveProfile()">
            <div class="form-field">
              <label class="field-label">כתובת אימייל</label>
              <div class="readonly-email-box" data-testid="profile-email-display">
                <span dir="ltr">{{ user.email }}</span>
                <span class="readonly-tag">לקריאה בלבד (חשבון Google)</span>
              </div>
            </div>

            <div class="form-row">
              <div class="form-field">
                <label for="profile-given-name" class="field-label">שם פרטי</label>
                <input
                  id="profile-given-name"
                  type="text"
                  class="form-input"
                  [(ngModel)]="editGivenName"
                  [appSavedField]="profileSavedTick"
                  name="givenName"
                  placeholder="שם פרטי"
                  data-testid="profile-given-name-input"
                />
              </div>

              <div class="form-field">
                <label for="profile-family-name" class="field-label">שם משפחה</label>
                <input
                  id="profile-family-name"
                  type="text"
                  class="form-input"
                  [(ngModel)]="editFamilyName"
                  [appSavedField]="profileSavedTick"
                  name="familyName"
                  placeholder="שם משפחה"
                  data-testid="profile-family-name-input"
                />
              </div>
            </div>

            @if (profileError) {
              <p class="error-msg" role="alert">{{ profileError }}</p>
            }

            <footer class="modal-footer">
              <button type="button" class="btn-secondary" (click)="close()">סגור</button>
              <button
                type="button"
                class="btn-primary"
                [disabled]="savingProfile"
                (click)="saveProfile()"
                data-testid="profile-save-button"
              >
                {{ savingProfile ? 'שומר…' : 'שמור שינויים' }}
              </button>
            </footer>
          </form>
        </div>
      </div>
    }

    <!-- 3. Groups Management Modal -->
    @if (modalType === 'groups') {
      <div class="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="groups-dialog-title" (click)="close()">
        <div class="modal-card modal-card--groups" (click)="$event.stopPropagation()">
          <header class="modal-header">
            <div class="title-with-icon">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#1f6435" stroke-width="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
              <div>
                <h2 id="groups-dialog-title">הקבוצות שלי</h2>
                <span class="profile-subtitle">שיתוף סלי קניות עם בן/בת הזוג, משפחה או חברים</span>
              </div>
            </div>
            <button type="button" class="modal-close-btn" (click)="close()" aria-label="סגירה">×</button>
          </header>

          <div class="modal-body">
            <!-- Pending Invites -->
            @if (pendingInvites.length > 0) {
              <div class="invites-alert" data-testid="pending-invites-alert">
                <h3 class="invites-alert-title">הזמנות לקבוצה הממתינות לך:</h3>
                @for (inv of pendingInvites; track inv.groupId) {
                  <div class="invite-item">
                    <div class="invite-info">
                      <strong>{{ inv.groupName }}</strong>
                      <span class="invite-sub">הוזמנת על ידי {{ inv.inviterName }}</span>
                    </div>
                    <div class="invite-actions">
                      <button type="button" class="btn-primary btn-sm" (click)="acceptInvite(inv.groupId)">
                        הצטרפו
                      </button>
                      <button type="button" class="btn-secondary btn-sm" (click)="declineInvite(inv.groupId)">
                        דחו
                      </button>
                    </div>
                  </div>
                }
              </div>
            }

            <!-- Current Groups List -->
            @if (loadingGroups) {
              <div class="loading-state">טוען קבוצות…</div>
            } @else {
              @if (groups.length === 0) {
                <div class="empty-groups-box">
                  <p>אין עדיין קבוצה.</p>
                  <p class="empty-sub">איך לקרוא לקבוצה הראשונה?</p>
                </div>
              } @else {
                <div class="groups-list">
                  @for (grp of groups; track grp.id) {
                    <article class="group-item-card" data-testid="group-item-card">
                      <div class="group-card-header">
                        <div class="group-title-row">
                          <h3 class="group-name">{{ grp.name }}</h3>
                          <div class="badge-row">
                            @if (grp.isDefault) {
                              <span class="badge badge-default">ברירת מחדל לסל</span>
                            }
                            <span class="badge badge-role">{{ grp.role === 'owner' ? 'בעלים' : 'עורך' }}</span>
                          </div>
                        </div>

                        <div class="group-header-actions">
                          <button
                            type="button"
                            class="btn-text-action"
                            (click)="toggleDefaultGroup(grp)"
                            title="הגדרה או ביטול קבוצה זו כברירת מחדל לסל שלך"
                          >
                            {{ grp.isDefault ? 'בטל ברירת מחדל' : 'הגדר כברירת מחדל' }}
                          </button>

                          @if (grp.role === 'owner') {
                            <button
                              type="button"
                              class="btn-text-danger"
                              (click)="deleteGroup(grp)"
                              title="מחיקת הקבוצה"
                            >
                              מחק קבוצה
                            </button>
                          } @else {
                            <button
                              type="button"
                              class="btn-text-danger"
                              (click)="leaveGroup(grp)"
                              title="יציאה מהקבוצה"
                            >
                              עזוב קבוצה
                            </button>
                          }
                        </div>
                      </div>

                      <!-- Group Members -->
                      <div class="members-section">
                        <h4 class="members-title">חברים בקבוצה ({{ grp.members.length }}):</h4>
                        <ul class="members-list">
                          @for (m of grp.members; track m.id) {
                            <li class="member-row">
                              <span class="member-avatar">
                                @if (m.pictureUrl) {
                                  <img [src]="m.pictureUrl" alt="" class="member-img" />
                                } @else {
                                  <span class="member-fallback">{{ (m.givenName || m.invitedEmail || 'U')[0] }}</span>
                                }
                              </span>
                              <div class="member-meta">
                                <span class="member-name">
                                  {{ m.givenName ? (m.givenName + ' ' + (m.familyName || '')) : (m.invitedEmail || 'משתמש') }}
                                </span>
                                @if (m.status === 'invited') {
                                  <span class="badge badge-pending">ממתין להרשמה</span>
                                }
                                <span class="member-role-label">{{ m.role === 'owner' ? 'בעלים' : 'חבר' }}</span>
                              </div>

                              @if (grp.role === 'owner' && m.userId !== user?.id && m.userId) {
                                <button
                                  type="button"
                                  class="btn-text-danger btn-remove-member"
                                  (click)="removeMember(grp.id, m.userId)"
                                  title="הסרת חבר"
                                >
                                  הסר
                                </button>
                              }
                            </li>
                          }
                        </ul>
                      </div>

                      <!-- Invite Form -->
                      @if (grp.role === 'owner' || grp.role === 'editor') {
                        <div class="invite-form">
                          <input
                            type="email"
                            class="form-input form-input--sm"
                            [(ngModel)]="inviteEmails[grp.id]"
                            placeholder="כתובת Gmail להזמנה (למשל spouse@gmail.com)"
                            dir="ltr"
                          />
                          <button
                            type="button"
                            class="btn-primary btn-sm"
                            [disabled]="!inviteEmails[grp.id] || invitingId === grp.id"
                            (click)="sendInvite(grp.id)"
                          >
                            {{ invitingId === grp.id ? 'שולח…' : 'הזמן חבר' }}
                          </button>
                        </div>
                        @if (inviteNoticeGroupId === grp.id && inviteNotice) {
                          <p class="success-msg" role="status">{{ inviteNotice }}</p>
                        }
                        @if (inviteNoticeGroupId === grp.id && inviteError) {
                          <p class="error-msg" role="alert">{{ inviteError }}</p>
                        }
                      }
                    </article>
                  }
                </div>
              }
            }

            <!-- Create New Group -->
            <section class="create-group-box">
              <h3>{{ groups.length === 0 ? 'מה השם של הקבוצה הראשונה?' : 'יצירת קבוצה חדשה' }}</h3>
              <div class="create-group-inputs">
                <input
                  type="text"
                  class="form-input"
                  [(ngModel)]="newGroupName"
                  placeholder="שם הקבוצה (לדוגמה: הבית, משפחה)"
                  data-testid="create-group-name-input"
                />
                <label class="checkbox-label">
                  <input type="checkbox" [(ngModel)]="newGroupMakeDefault" />
                  <span>הגדר קבוצה זו כברירת מחדל לסל שלי</span>
                </label>
                <button
                  type="button"
                  class="btn-primary"
                  [disabled]="!newGroupName.trim() || creatingGroup"
                  (click)="createGroup()"
                  data-testid="create-group-confirm-button"
                >
                  {{ creatingGroup ? 'יוצר…' : 'צור קבוצה ושתף' }}
                </button>
              </div>
            </section>
          </div>
        </div>
      </div>
    }
  `,
  styles: [`
    .modal-overlay {
      position: fixed;
      inset: 0;
      background: rgba(15, 23, 42, 0.45);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1rem;
      z-index: 10000;
      direction: rtl;
    }

    .modal-card {
      background: #ffffff;
      border-radius: 12px;
      max-width: 520px;
      width: 100%;
      max-height: 90vh;
      overflow-y: auto;
      padding: 1.5rem;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    }
    .modal-card--groups {
      max-width: 600px;
    }
    .modal-card--login {
      max-width: 440px;
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1.25rem;
      padding-bottom: 0.75rem;
      border-bottom: 1px solid #e2e8f0;
    }

    .title-with-icon {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }
    .title-with-icon h2 {
      margin: 0;
      font-size: 1.25rem;
      color: #0f172a;
    }
    .profile-subtitle {
      font-size: 0.85rem;
      color: #64748b;
    }

    .modal-close-btn {
      background: none;
      border: none;
      font-size: 1.5rem;
      line-height: 1;
      color: #64748b;
      cursor: pointer;
      padding: 0.25rem;
    }

    .user-avatar-badge {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #1f6435;
      color: #fff;
      font-weight: 700;
      font-size: 1.1rem;
      flex-shrink: 0;
    }
    .avatar-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .form-field {
      margin-bottom: 1rem;
    }
    .field-label {
      display: block;
      font-weight: 600;
      font-size: 0.88rem;
      color: #334155;
      margin-bottom: 0.4rem;
    }

    .form-input {
      width: 100%;
      height: 40px;
      padding: 0 0.75rem;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      font-size: 0.95rem;
      box-sizing: border-box;
      direction: rtl;
    }
    .form-input--sm {
      height: 36px;
      font-size: 0.88rem;
    }
    .form-input:focus {
      border-color: #1f6435;
      outline: none;
    }

    .readonly-email-box {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 0.65rem 0.85rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 0.95rem;
      color: #1e293b;
    }
    .readonly-tag {
      font-size: 0.75rem;
      color: #64748b;
      background: #e2e8f0;
      padding: 0.2rem 0.5rem;
      border-radius: 4px;
    }

    .form-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.75rem;
    }

    .btn-primary, .btn-secondary {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      height: 40px;
      padding: 0 1.25rem;
      border-radius: 8px;
      font-weight: 600;
      font-size: 0.9rem;
      cursor: pointer;
      transition: background-color 0.15s ease;
      white-space: nowrap;
    }
    .btn-sm {
      height: 32px;
      padding: 0 0.85rem;
      font-size: 0.82rem;
    }

    .btn-primary {
      background: #1f6435;
      color: #ffffff;
      border: 1px solid #1f6435;
    }
    .btn-primary:hover:not(:disabled) {
      background: #184e29;
    }

    .btn-secondary {
      background: #f8fafc;
      color: #334155;
      border: 1px solid #cbd5e1;
    }
    .btn-secondary:hover:not(:disabled) {
      background: #e2e8f0;
    }

    .btn-text-action {
      background: none;
      border: none;
      color: #1f6435;
      font-weight: 600;
      font-size: 0.85rem;
      cursor: pointer;
      padding: 0.2rem 0.4rem;
    }
    .btn-text-danger {
      background: none;
      border: none;
      color: #dc2626;
      font-weight: 600;
      font-size: 0.85rem;
      cursor: pointer;
      padding: 0.2rem 0.4rem;
    }

    .modal-footer {
      display: flex;
      justify-content: flex-end;
      gap: 0.75rem;
      margin-top: 1.25rem;
      padding-top: 1rem;
      border-top: 1px solid #e2e8f0;
    }

    .error-msg {
      color: #dc2626;
      font-size: 0.88rem;
      margin: 0.5rem 0 0;
    }
    .success-msg {
      color: #166534;
      font-size: 0.88rem;
      margin: 0.5rem 0 0;
    }

    /* Groups */
    .invites-alert {
      background: #eff6ff;
      border: 1px solid #bfdbfe;
      border-radius: 10px;
      padding: 1rem;
      margin-bottom: 1.25rem;
    }
    .invites-alert-title {
      margin: 0 0 0.5rem;
      font-size: 0.95rem;
      color: #1e40af;
    }
    .invite-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #ffffff;
      padding: 0.65rem 0.85rem;
      border-radius: 8px;
      margin-top: 0.4rem;
    }
    .invite-actions {
      display: flex;
      gap: 0.4rem;
    }

    .groups-list {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      margin-bottom: 1.5rem;
    }
    .group-item-card {
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      padding: 1rem;
      background: #fafafa;
    }
    .group-card-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 0.75rem;
      flex-wrap: wrap;
      gap: 0.5rem;
    }
    .group-name {
      margin: 0 0 0.25rem;
      font-size: 1.1rem;
      color: #0f172a;
    }
    .badge-row {
      display: flex;
      gap: 0.35rem;
    }
    .badge {
      display: inline-block;
      padding: 0.15rem 0.5rem;
      border-radius: 6px;
      font-size: 0.75rem;
      font-weight: 600;
    }
    .badge-default {
      background: #dcfce7;
      color: #166534;
      border: 1px solid #bbf7d0;
    }
    .badge-role {
      background: #f1f5f9;
      color: #475569;
    }
    .badge-pending {
      background: #fef3c7;
      color: #92400e;
    }

    .members-section {
      margin-bottom: 0.75rem;
    }
    .members-title {
      margin: 0 0 0.4rem;
      font-size: 0.85rem;
      color: #64748b;
    }
    .members-list {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 0.4rem;
    }
    .member-row {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      background: #ffffff;
      padding: 0.4rem 0.6rem;
      border-radius: 6px;
      font-size: 0.88rem;
    }
    .member-avatar {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #cbd5e1;
      color: #334155;
      font-size: 0.8rem;
      font-weight: 700;
    }
    .member-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .member-meta {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
    }
    .member-role-label {
      font-size: 0.75rem;
      color: #94a3b8;
    }

    .invite-form {
      display: flex;
      gap: 0.5rem;
      margin-top: 0.75rem;
      border-top: 1px dashed #e2e8f0;
      padding-top: 0.75rem;
    }
    .invite-form input {
      flex: 1;
    }

    .create-group-box {
      border: 1px dashed #cbd5e1;
      border-radius: 10px;
      padding: 1.25rem;
      background: #ffffff;
    }
    .create-group-box h3 {
      margin: 0 0 0.75rem;
      font-size: 1rem;
      color: #1e293b;
    }
    .create-group-inputs {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }
    .checkbox-label {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.88rem;
      color: #475569;
      cursor: pointer;
    }

    /* Login */
    .login-title-icon {
      transform: scaleX(-1);
    }
    .login-intro {
      color: #475569;
      font-size: 0.95rem;
      line-height: 1.5;
      margin-bottom: 1.5rem;
      text-align: center;
    }
    .google-btn-wrapper {
      display: flex;
      justify-content: center;
      min-height: 44px;
      margin-bottom: 1rem;
    }
    .login-loading-hint {
      text-align: center;
      font-size: 0.88rem;
      color: #1f6435;
      margin: 0.5rem 0 0;
    }
    .login-terms-note {
      text-align: center;
      font-size: 0.78rem;
      color: #64748b;
      margin: 0.75rem 0 0;
      line-height: 1.4;
    }
    .login-terms-note a {
      color: #0284c7;
      text-decoration: underline;
    }
    .login-terms-note a:hover {
      color: #0369a1;
    }

    @media (max-width: 860px) {
      .modal-card {
        padding: 1.25rem 1rem;
        border-radius: 12px;
      }
      .form-row {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class AccountModalsComponent implements OnInit, OnChanges {
  @Input() modalType: 'login' | 'profile' | 'groups' | null = null;
  @Output() modalTypeChange = new EventEmitter<'login' | 'profile' | 'groups' | null>();

  readonly auth = inject(AuthService);
  private readonly localProfile = inject(LocalProfileService);
  private readonly ngZone = inject(NgZone);

  user: UserProfile | null = null;

  // Profile fields
  editGivenName = '';
  editFamilyName = '';
  savingProfile = false;
  profileError = '';
  profileSavedTick = 0;

  // Groups
  groups: ShoppingGroupDetails[] = [];
  pendingInvites: PendingGroupInvite[] = [];
  loadingGroups = false;
  creatingGroup = false;
  newGroupName = '';
  newGroupMakeDefault = true;
  inviteEmails: Record<number, string> = {};
  invitingId: number | null = null;

  // Login
  googleClientAvailable = false;
  hasGoogleClientId = false;
  loggingIn = false;
  loginError = '';

  ngOnInit(): void {
    this.auth.currentUser$.subscribe((u) => {
      this.user = u;
      if (u) {
        this.editGivenName = u.givenName ?? '';
        this.editFamilyName = u.familyName ?? '';
      }
    });

    if (this.modalType === 'groups') {
      this.loadGroupsData();
    } else if (this.modalType === 'login') {
      void this.setupGoogleSignIn();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['modalType']) {
      if (this.modalType === 'groups') {
        this.loadGroupsData();
      } else if (this.modalType === 'login') {
        this.loginError = '';
        this.loggingIn = false;
        void this.setupGoogleSignIn();
      }
    }
  }

  close(): void {
    this.modalType = null;
    this.modalTypeChange.emit(null);
  }

  // ── Profile ─────────────────────────────────────────────────────────────

  saveProfile(): void {
    if (!this.user) return;
    this.savingProfile = true;
    this.profileError = '';

    this.auth.updateProfile({
      givenName: this.editGivenName.trim(),
      familyName: this.editFamilyName.trim()
    }).then(() => {
      this.savingProfile = false;
      this.profileSavedTick++;
    }).catch((err) => {
      this.savingProfile = false;
      this.profileError = err?.error?.error || 'אירעה שגיאה בשמירת הפרטים.';
    });
  }

  // ── Groups ──────────────────────────────────────────────────────────────

  loadGroupsData(): void {
    this.loadingGroups = true;
    this.auth.getGroups().subscribe({
      next: (g) => {
        this.groups = g;
        this.loadingGroups = false;
      },
      error: () => {
        this.loadingGroups = false;
      }
    });

    this.auth.getPendingInvites().subscribe({
      next: (inv) => {
        this.pendingInvites = inv;
      }
    });
  }

  createGroup(): void {
    if (!this.newGroupName.trim()) return;
    this.creatingGroup = true;
    this.auth.createGroup(this.newGroupName.trim(), this.newGroupMakeDefault).subscribe({
      next: () => {
        this.creatingGroup = false;
        this.newGroupName = '';
        this.loadGroupsData();
      },
      error: () => {
        this.creatingGroup = false;
      }
    });
  }

  toggleDefaultGroup(grp: ShoppingGroupDetails): void {
    this.auth.setDefaultGroup(grp.id).subscribe({
      next: () => {
        this.loadGroupsData();
      }
    });
  }

  deleteGroup(grp: ShoppingGroupDetails): void {
    if (!confirm(`האם למחוק את הקבוצה "${grp.name}"? סלים ששויכו אליה יהפכו לאישיים.`)) {
      return;
    }
    this.auth.deleteGroup(grp.id).subscribe({
      next: () => {
        this.loadGroupsData();
      }
    });
  }

  leaveGroup(grp: ShoppingGroupDetails): void {
    if (!this.user || !confirm(`האם לעזוב את הקבוצה "${grp.name}"?`)) {
      return;
    }
    this.auth.removeMember(grp.id, this.user.id).subscribe({
      next: () => {
        this.loadGroupsData();
      }
    });
  }

  inviteNotice = '';
  inviteError = '';
  inviteNoticeGroupId: number | null = null;

  sendInvite(groupId: number): void {
    const email = (this.inviteEmails[groupId] || '').trim();
    if (!email || !email.includes('@')) return;

    this.invitingId = groupId;
    this.inviteNotice = '';
    this.inviteError = '';
    this.inviteNoticeGroupId = groupId;
    this.auth.inviteMember(groupId, email).subscribe({
      next: () => {
        this.invitingId = null;
        this.inviteEmails[groupId] = '';
        this.inviteNotice = `נשלח אימייל עם קישור אל ${email}`;
        this.loadGroupsData();
      },
      error: (err) => {
        this.invitingId = null;
        this.inviteError = err?.error?.error || 'לא הצלחנו לשלוח את ההזמנה.';
      }
    });
  }

  acceptInvite(groupId: number): void {
    this.auth.acceptInvite(groupId).subscribe({
      next: () => {
        this.loadGroupsData();
      }
    });
  }

  declineInvite(groupId: number): void {
    this.auth.declineInvite(groupId).subscribe({
      next: () => {
        this.loadGroupsData();
      }
    });
  }

  removeMember(groupId: number, targetUserId: number): void {
    if (!confirm('האם להסיר חבר זה מהקבוצה?')) return;
    this.auth.removeMember(groupId, targetUserId).subscribe({
      next: () => {
        this.loadGroupsData();
      }
    });
  }

  // ── Login ───────────────────────────────────────────────────────────────

  /**
   * Phones/tablets use Google's redirect mode. In popup mode iOS Safari opens the account
   * chooser as a new tab and may unload this tab meanwhile, so the credential never comes
   * back and the user is left on a blank Google tab.
   */
  private useRedirectSignIn(): boolean {
    const ua = navigator.userAgent;
    const isIpadOs = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
    return /iPhone|iPad|iPod|Android/i.test(ua) || isIpadOs;
  }

  /** Short-lived cookie read by POST /api/auth/google/redirect to claim this device's basket. */
  private writeLoginContextCookie(): void {
    const ctx = JSON.stringify({
      b: this.localProfile.getShoppingBasketId() ?? null,
      t: this.localProfile.getShoppingBasketClaimToken() ?? null
    });
    // Google's POST back is a cross-site navigation, so the cookie needs SameSite=None (requires HTTPS).
    const sameSite = location.protocol === 'https:' ? 'SameSite=None; Secure' : 'SameSite=Lax';
    document.cookie = `commpare_login_ctx=${encodeURIComponent(ctx)}; Path=/api/auth/google; Max-Age=900; ${sameSite}`;
  }

  private async setupGoogleSignIn(): Promise<void> {
    if (this.auth.pendingLoginError) {
      this.loginError = this.auth.pendingLoginError;
      this.auth.pendingLoginError = '';
    }
    const config = await this.auth.getAuthConfig();
    this.hasGoogleClientId = Boolean(config.clientId && config.clientId.trim().length > 0);
    const redirectMode = this.useRedirectSignIn();

    if (!this.hasGoogleClientId) {
      this.googleClientAvailable = false;
      this.loginError = 'התחברות באמצעות Google אינה מוגדרת כרגע בשרת.';
      return;
    }

    let attempts = 0;
    const tryRender = () => {
      attempts++;
      if (typeof google !== 'undefined' && google?.accounts?.id) {
        const container = document.getElementById('google-signin-btn-container');
        if (container) {
          try {
            if (redirectMode) {
              this.writeLoginContextCookie();
            }
            google.accounts.id.initialize({
              client_id: config.clientId.trim(),
              // Must exactly match an "Authorized redirect URI" in Google Cloud Console.
              ...(redirectMode
                ? { ux_mode: 'redirect', login_uri: `${location.origin}/api/auth/google/redirect` }
                : {}),
              callback: (response: { credential?: string }) => {
                this.ngZone.run(() => this.handleGoogleCredentialResponse(response));
              },
              auto_select: false,
              cancel_on_tap_outside: true
            });

            container.innerHTML = '';
            google.accounts.id.renderButton(container, {
              type: 'standard',
              theme: 'outline',
              size: 'large',
              text: 'signin_with',
              shape: 'rectangular',
              logo_alignment: 'left',
              width: 280,
              locale: 'he'
            });
            this.googleClientAvailable = true;
            return;
          } catch (err) {
            console.warn('Google Identity Services render failed:', err);
          }
        }
      }

      if (attempts < 25 && this.modalType === 'login') {
        setTimeout(tryRender, 80);
      } else {
        this.googleClientAvailable = false;
      }
    };

    setTimeout(tryRender, 50);
  }

  private handleGoogleCredentialResponse(response: { credential?: string }): void {
    const credential = response?.credential;
    if (!credential) {
      this.loginError = 'לא התקבל מזהה אימות מ-Google.';
      return;
    }

    this.loggingIn = true;
    this.loginError = '';
    const deviceBasketId = this.localProfile.getShoppingBasketId();
    const deviceBasketToken = this.localProfile.getShoppingBasketClaimToken();

    this.auth.loginWithGoogle(credential, deviceBasketId, deviceBasketToken)
      .then(() => {
        this.loggingIn = false;
        this.close();
      })
      .catch((err) => {
        this.loggingIn = false;
        this.loginError = err?.error?.error || 'אירעה שגיאה בהתחברות באמצעות Google.';
      });
  }
}
